import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle it first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("hstc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, marketing_consent = true, ref = null) => {
    const { data } = await api.post("/auth/register", { name, email, password, marketing_consent, ref });
    if (data.token) localStorage.setItem("hstc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const exchangeGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    if (data.token) localStorage.setItem("hstc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    // Best-effort server logout; local state is cleared regardless of API result.
    try { await api.post("/auth/logout"); } catch (err) { console.error("Logout request failed:", err); }
    localStorage.removeItem("hstc_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, exchangeGoogle, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
